/**
 * PSLF Debug Scenarios — granular overrides for testing every visual state
 * of HeroDashboard and PslfRecommendations without needing specific real users.
 *
 * Stored in URL params + localStorage so debug URLs can be shared with the team.
 * Activated by `?pslf_debug=true` in the URL.
 */
import type { LoanSummary, SummaryByLoanType } from 'tio-common/types/loan-summary';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type DebugFormStatus =
  | 'created'
  | 'borrower_signed'
  | 'employer_signed'
  | 'awaiting_submit'
  | 'submit_pending'
  | 'submitted'
  | 're_submitted'
  | 'rejected'
  | 'cancelled';

export const DEBUG_FORM_STATUSES: { value: DebugFormStatus; label: string }[] = [
  { value: 'created', label: 'Created' },
  { value: 'borrower_signed', label: 'Borrower Signed' },
  { value: 'employer_signed', label: 'Employer Signed' },
  { value: 'awaiting_submit', label: 'Awaiting Submit' },
  { value: 'submit_pending', label: 'Submit Pending' },
  { value: 'submitted', label: 'Submitted' },
  { value: 're_submitted', label: 'Re-submitted' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'cancelled', label: 'Cancelled' },
];

export interface DebugForm {
  id: string;
  formType: 'currentEmployer' | 'priorEmployer';
  status: DebugFormStatus;
  employerName: string;
  borrowerCompletedAt: string | null;
  employerCompletedAt: string | null;
  submittedAt: string | null;
}

export interface DebugLoan {
  name: string;
  loanType: 'direct' | 'ffel';
  currentBalance: number;
  qualifyingPayments: number;
}

export interface PslfDebugState {
  enabled: boolean;
  debugForms: DebugForm[];
  hasNsldsData: boolean;
  debugLoans: DebugLoan[];
  estimatorCompleted: boolean;
  showRecommendations: boolean;
  consolidateLoans: boolean;
  missingRepaymentPlan: boolean;
  changeRepaymentPlan: boolean;
  linkLoans: boolean;
  recertifyIncome: boolean;
}

const STORAGE_KEY = 'pslf-debug-state';
const URL_PARAM = 'pslf_debug';
const URL_STATE_PARAM = 'pslf_debug_state'; // shareable URL-encoded state for repros
const STATE_TTL_MS = 24 * 60 * 60 * 1000; // 24h — auto-expire so a forgotten dev session can't haunt later QA

interface StoredDebugEnvelope {
  state: PslfDebugState;
  setAt: number;
}

function readEnvelope(): StoredDebugEnvelope | null {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (!stored) return null;
  try {
    const parsed = JSON.parse(stored) as Partial<StoredDebugEnvelope> & Partial<PslfDebugState>;
    // Backward compat: older versions stored PslfDebugState directly
    if ('state' in parsed && parsed.state) {
      return parsed as StoredDebugEnvelope;
    }
    return { state: parsed as PslfDebugState, setAt: Date.now() };
  } catch {
    return null;
  }
}

function isExpired(envelope: StoredDebugEnvelope): boolean {
  if (!Number.isFinite(envelope.setAt)) return true;
  return Date.now() - envelope.setAt > STATE_TTL_MS;
}

// debugLoans can grow unbounded via the UI; keep it in localStorage only so
// the shareable URL stays well under common length limits.
const URL_OMITTED_KEYS: (keyof PslfDebugState)[] = ['debugLoans', 'debugForms'];

function toBase64Url(value: string): string {
  return btoa(value).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function fromBase64Url(value: string): string {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized.padEnd(normalized.length + ((4 - (normalized.length % 4)) % 4), '=');
  return atob(padded);
}

/**
 * Encode the diff between current state and defaults as a URL-safe base64 payload
 * so developers can share a single URL that reproduces a debug scenario without
 * tripping common URL length limits. Large fields (debugLoans) stay in localStorage.
 */
function encodeStateForUrl(state: PslfDebugState): string {
  const defaults = getDefaultDebugState();
  const diff: Partial<PslfDebugState> = {};
  (Object.keys(state) as (keyof PslfDebugState)[]).forEach((key) => {
    if (URL_OMITTED_KEYS.includes(key)) return;
    if (JSON.stringify(state[key]) !== JSON.stringify(defaults[key])) {
      // Object spread keeps the key's value type intact under exactOptionalPropertyTypes.
      Object.assign(diff, { [key]: state[key] });
    }
  });
  return toBase64Url(encodeURIComponent(JSON.stringify(diff)));
}

function decodeStateFromUrl(encoded: string): Partial<PslfDebugState> | null {
  try {
    return JSON.parse(decodeURIComponent(fromBase64Url(encoded))) as Partial<PslfDebugState>;
  } catch {
    // Backward compat: older URLs used plain base64 (not URL-safe)
    try {
      return JSON.parse(decodeURIComponent(atob(encoded))) as Partial<PslfDebugState>;
    } catch {
      return null;
    }
  }
}

function readUrlState(): Partial<PslfDebugState> | null {
  if (typeof window === 'undefined') return null;
  const encoded = new URLSearchParams(window.location.search).get(URL_STATE_PARAM);
  return encoded ? decodeStateFromUrl(encoded) : null;
}

// ---------------------------------------------------------------------------
// Magic numbers — adjust here to change the visual demo across all scenarios
// ---------------------------------------------------------------------------

/** Hard-coded values used when estimatorCompleted is on. Keep these readable for screenshots. */
export const DEBUG_RECOMMENDATION = {
  estimatedMonthlyPayment: 285,
  estimatedForgivenessAmount: 62612.86,
  estimatedForgivenessDate: 2035,
  // Best-plan summary (cents): payoff 30,780 / 108 mo / $285/mo / interest 15,780 / remaining 62,612.86
  bestPayoffAmount: 3_078_000,
  bestTermMonths: 108,
  bestMonthlyPayments: 28_500,
  bestTotalInterestPaid: 1_578_000,
  bestRemLoanBalance: 6_261_286,
  // Current-plan baseline (cents)
  currentPayoffAmount: 10_500_000,
  currentTermMonths: 300,
  currentMonthlyPayments: 35_000,
  currentTotalInterestPaid: 4_250_000,
  currentRemLoanBalance: 0,
  // Estimator results (dollars)
  estimatorEstimatedMonthlyPayment: 285,
  estimatorEstimatedForgivenessAmount: 62340,
  estimatorRepaymentPlan: 'IBR',
} as const;

/** Compute the estimator's forgiveness date as ~10 years from today, ISO string. */
export function debugEstimatorForgivenessDate(): string {
  const d = new Date();
  d.setFullYear(d.getFullYear() + 10);
  return d.toISOString().slice(0, 10);
}

// ---------------------------------------------------------------------------
// Default state
// ---------------------------------------------------------------------------

export const DEFAULT_DEBUG_LOAN: DebugLoan = {
  name: 'Direct Loan',
  loanType: 'direct',
  currentBalance: 45000,
  qualifyingPayments: 45,
};

export function makeDebugFormId(): string {
  const uuid = globalThis.crypto?.randomUUID?.();
  if (uuid) {
    return `debug-form-${uuid}`;
  }
  return `debug-form-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

export const DEFAULT_DEBUG_FORM: DebugForm = {
  id: 'debug-form-1',
  formType: 'currentEmployer',
  status: 'submitted',
  employerName: 'Debug Employer',
  borrowerCompletedAt: null,
  employerCompletedAt: null,
  submittedAt: null,
};

export function getDefaultDebugState(): PslfDebugState {
  return {
    enabled: false,
    debugForms: [],
    hasNsldsData: false,
    debugLoans: [{ ...DEFAULT_DEBUG_LOAN }],
    estimatorCompleted: false,
    showRecommendations: false,
    consolidateLoans: false,
    missingRepaymentPlan: false,
    changeRepaymentPlan: false,
    linkLoans: false,
    recertifyIncome: false,
  };
}

// ---------------------------------------------------------------------------
// Read / write
// ---------------------------------------------------------------------------

export function isPslfDebugActive(): boolean {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  if (params.get(URL_PARAM) === 'true') return true;
  if (params.get(URL_STATE_PARAM)) return true;
  const envelope = readEnvelope();
  if (!envelope) return false;
  if (isExpired(envelope)) {
    localStorage.removeItem(STORAGE_KEY);
    return false;
  }
  return envelope.state.enabled;
}

export function getPslfDebugState(): PslfDebugState {
  if (typeof window === 'undefined') return getDefaultDebugState();

  // URL-encoded state wins over localStorage so a shared repro link is authoritative.
  // However, fields in URL_OMITTED_KEYS (e.g. debugLoans) are intentionally excluded
  // from the URL to keep it short — pull those from localStorage so they survive reload.
  const urlState = readUrlState();
  if (urlState) {
    const envelope = readEnvelope();
    if (envelope && isExpired(envelope)) {
      localStorage.removeItem(STORAGE_KEY);
    }
    const localState = envelope && !isExpired(envelope) ? envelope.state : null;
    const omittedFields: Partial<PslfDebugState> = {};
    if (localState) {
      for (const key of URL_OMITTED_KEYS) {
        Object.assign(omittedFields, { [key]: localState[key] });
      }
    }
    return { ...getDefaultDebugState(), ...omittedFields, ...urlState, enabled: true };
  }

  const envelope = readEnvelope();
  if (envelope && !isExpired(envelope)) {
    return { ...getDefaultDebugState(), ...envelope.state };
  }
  if (envelope && isExpired(envelope)) {
    localStorage.removeItem(STORAGE_KEY);
  }
  return getDefaultDebugState();
}

export function setPslfDebugState(state: PslfDebugState): void {
  if (typeof window === 'undefined') return;

  const envelope: StoredDebugEnvelope = { state, setAt: Date.now() };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(envelope));

  // Reload to re-run the route model hook with new overrides
  const url = new URL(window.location.href);
  if (state.enabled) {
    url.searchParams.set(URL_PARAM, 'true');
    url.searchParams.set(URL_STATE_PARAM, encodeStateForUrl(state));
  } else {
    url.searchParams.delete(URL_PARAM);
    url.searchParams.delete(URL_STATE_PARAM);
  }
  // Clean up any leftover demo_pslf_scenario URL param
  url.searchParams.delete('demo_pslf_scenario');
  window.location.assign(url.toString());
}

export function clearPslfDebugState(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(STORAGE_KEY);
  const url = new URL(window.location.href);
  url.searchParams.delete(URL_PARAM);
  url.searchParams.delete(URL_STATE_PARAM);
  url.searchParams.delete('debug');
  url.searchParams.delete('demo_pslf_scenario');
  window.location.assign(url.toString());
}

// ---------------------------------------------------------------------------
// Presets — each preset uses debugForms[] so users can see/edit what it produces
// ---------------------------------------------------------------------------

function isoMonthsAgo(months: number): string {
  const d = new Date();
  d.setMonth(d.getMonth() - months);
  return d.toISOString();
}

function submittedForm(id: string, overrides: Partial<DebugForm> = {}): DebugForm {
  const twoMonthsAgo = isoMonthsAgo(2);
  return {
    id,
    formType: 'currentEmployer',
    status: 'submitted',
    employerName: 'Debug Employer',
    borrowerCompletedAt: twoMonthsAgo,
    employerCompletedAt: twoMonthsAgo,
    submittedAt: twoMonthsAgo,
    ...overrides,
  };
}

function annualReminderForm(id: string): DebugForm {
  const elevenMonthsAgo = isoMonthsAgo(11);
  return {
    id,
    formType: 'currentEmployer',
    status: 'submitted',
    employerName: 'Debug Employer',
    borrowerCompletedAt: elevenMonthsAgo,
    employerCompletedAt: elevenMonthsAgo,
    submittedAt: elevenMonthsAgo,
  };
}

export const PSLF_DEBUG_PRESETS: Record<string, { label: string; state: Partial<PslfDebugState> }> =
  {
    blankSlate: {
      label: 'Blank Slate',
      state: {
        enabled: true,
        debugForms: [],
      },
    },
    allRecs: {
      label: 'All Recommendations',
      state: {
        enabled: true,
        debugForms: [submittedForm('debug-form-1')],
        hasNsldsData: true,
        debugLoans: [
          {
            name: 'Direct Loan',
            loanType: 'direct',
            currentBalance: 45000,
            qualifyingPayments: 45,
          },
          { name: 'FFEL Loan', loanType: 'ffel', currentBalance: 22000, qualifyingPayments: 30 },
        ],
        estimatorCompleted: true,
        showRecommendations: true,
        consolidateLoans: true,
        missingRepaymentPlan: true,
        changeRepaymentPlan: true,
        linkLoans: true,
        recertifyIncome: true,
      },
    },
    annualRenewal: {
      label: 'Annual Renewal',
      state: {
        enabled: true,
        debugForms: [annualReminderForm('debug-form-1')],
        hasNsldsData: true,
        showRecommendations: true,
        recertifyIncome: true,
      },
    },
    withNslds: {
      label: 'With NSLDS',
      state: {
        enabled: true,
        debugForms: [submittedForm('debug-form-1')],
        hasNsldsData: true,
        debugLoans: [
          {
            name: 'Direct Loan',
            loanType: 'direct',
            currentBalance: 45000,
            qualifyingPayments: 72,
          },
          {
            name: 'Direct Loan 2',
            loanType: 'direct',
            currentBalance: 15000,
            qualifyingPayments: 45,
          },
        ],
        showRecommendations: true,
        consolidateLoans: true,
        missingRepaymentPlan: true,
      },
    },
    estimatorDone: {
      label: 'Estimator Completed',
      state: {
        enabled: true,
        debugForms: [submittedForm('debug-form-1')],
        hasNsldsData: true,
        estimatorCompleted: true,
        showRecommendations: true,
        recertifyIncome: true,
      },
    },
    noNslds: {
      label: 'No NSLDS Data',
      state: {
        enabled: true,
        debugForms: [submittedForm('debug-form-1')],
        hasNsldsData: false,
      },
    },
    formRejected: {
      label: 'Form Rejected',
      state: {
        enabled: true,
        debugForms: [submittedForm('debug-form-1', { status: 'rejected', submittedAt: null })],
      },
    },
    formNoNslds: {
      label: 'Form Submitted, No NSLDS',
      state: {
        enabled: true,
        debugForms: [submittedForm('debug-form-1')],
        hasNsldsData: false,
        showRecommendations: true,
        linkLoans: true,
      },
    },
    borrowerSigned: {
      label: 'Borrower Signed',
      state: {
        enabled: true,
        debugForms: [
          submittedForm('debug-form-1', {
            status: 'borrower_signed',
            employerCompletedAt: null,
            submittedAt: null,
          }),
        ],
      },
    },
    employerSigned: {
      label: 'Employer Signed',
      state: {
        enabled: true,
        debugForms: [
          submittedForm('debug-form-1', { status: 'employer_signed', submittedAt: null }),
        ],
      },
    },
    multiForm: {
      label: 'Multiple Forms',
      state: {
        enabled: true,
        debugForms: [
          submittedForm('debug-form-1'),
          submittedForm('debug-form-2', {
            formType: 'priorEmployer',
            employerName: 'Previous Corp',
          }),
        ],
      },
    },
  };

// ---------------------------------------------------------------------------
// Mock data factories — used by the route to override model data
// ---------------------------------------------------------------------------

function makeLoanTypeSummary(overrides: Partial<SummaryByLoanType>): SummaryByLoanType {
  return {
    loanType: 'direct',
    loanIds: ['loan-001'],
    currentBalance: 45000,
    interestRate: 5.5,
    monthlyPayment: 350,
    repaymentPlan: 'ibr',
    repaymentPlans: ['ibr'],
    totalDisbursedAmount: 50000,
    federalLoanTime: '120',
    ...overrides,
  };
}

/** Loan summary where all loans are direct with PSLF-eligible plans (no recs triggered) */
export function mockLoanSummaryClean(): LoanSummary {
  return {
    summaryByLoanType: [makeLoanTypeSummary({})],
    errors: [],
  };
}

/** Has non-direct loans → triggers "consolidate loans" recommendation */
export function mockLoanSummaryNonDirect(): LoanSummary {
  return {
    summaryByLoanType: [
      makeLoanTypeSummary({ loanType: 'direct' }),
      makeLoanTypeSummary({
        loanType: 'ffel',
        loanIds: ['loan-002'],
        currentBalance: 22000,
        repaymentPlan: 'standard',
        repaymentPlans: ['standard'],
      }),
    ],
    errors: [],
  };
}

/** Has a loan with no repayment plan → triggers "provide repayment plan" recommendation */
export function mockLoanSummaryMissingPlan(): LoanSummary {
  return {
    summaryByLoanType: [
      makeLoanTypeSummary({
        repaymentPlan: 'noRepaymentPlan',
        repaymentPlans: ['noRepaymentPlan'],
      }),
    ],
    errors: [],
  };
}

/** All plans are non-PSLF-eligible → triggers "change repayment plan" recommendation */
export function mockLoanSummaryNonEligiblePlan(): LoanSummary {
  return {
    summaryByLoanType: [
      makeLoanTypeSummary({ repaymentPlan: 'standard', repaymentPlans: ['standard'] }),
      makeLoanTypeSummary({
        loanIds: ['loan-003'],
        repaymentPlan: 'graduated',
        repaymentPlans: ['graduated'],
      }),
    ],
    errors: [],
  };
}

/**
 * Build a composite mock LoanSummary driven by the debug state flags.
 * When multiple recommendation flags are on, we pick the most
 * "interesting" loan summary that satisfies all of them.
 *
 * NOTE on parity: this returns a `LoanSummary` typed value, so the static
 * contract with consumers (PslfRecommendations, ForgivenessEstimator, etc.)
 * is enforced at compile time. If the live adapter response shape changes
 * (`adapterFor('user').getLoanSummary`), update the `LoanSummary` interface
 * in `tio-common/src/types/loan-summary.ts` and TypeScript will flag every
 * mock that needs adjusting.
 */
export function buildMockLoanSummary(debugState: PslfDebugState): LoanSummary {
  // Priority: missingRepaymentPlan (blocks changeRepaymentPlan), consolidate, changeRepaymentPlan
  const summaries: SummaryByLoanType[] = [];

  if (debugState.missingRepaymentPlan) {
    summaries.push(
      makeLoanTypeSummary({
        repaymentPlan: 'noRepaymentPlan',
        repaymentPlans: ['noRepaymentPlan'],
      })
    );
  }

  if (debugState.consolidateLoans) {
    summaries.push(
      makeLoanTypeSummary({
        loanType: 'ffel',
        loanIds: ['loan-debug-ffel'],
        currentBalance: 22000,
        repaymentPlan: debugState.missingRepaymentPlan ? 'noRepaymentPlan' : 'standard',
        repaymentPlans: [debugState.missingRepaymentPlan ? 'noRepaymentPlan' : 'standard'],
      })
    );
  }

  if (debugState.changeRepaymentPlan && !debugState.missingRepaymentPlan) {
    summaries.push(
      makeLoanTypeSummary({
        repaymentPlan: 'standard',
        repaymentPlans: ['standard'],
      })
    );
  }

  // If nothing was added, provide a clean direct loan
  if (summaries.length === 0) {
    summaries.push(makeLoanTypeSummary({}));
  }

  return { summaryByLoanType: summaries, errors: [] };
}
